// Centralised FAQ content. To edit a question, update the relevant entry below.
// Wording has been reviewed against the actual VinePass feature set
// (2026-06-01). Items that don't reflect a currently shipped feature are
// either rewritten to match reality or marked `comingSoon: true` so the UI
// can flag them gracefully.

export const GUEST_FAQS = [
  {
    q: 'What is VinePass?',
    a: 'VinePass is an online ticketing platform for curated wine farm events, tastings, experiences and lifestyle gatherings across South Africa. We focus on premium, hand-picked experiences so every booking feels considered.',
  },
  {
    q: 'How do I buy tickets on VinePass?',
    a: 'Browse the experiences on our Events page, open the one you would like to attend, choose your ticket type and quantity, then complete your payment securely online via PayFast.',
  },
  {
    q: 'How will I receive my ticket?',
    a: 'After a successful payment we email your ticket(s) instantly. Each ticket includes a unique QR code that is scanned at the event entrance. Tickets are also always available in your VinePass account under "My Tickets".',
  },
  {
    q: 'Do I need to print my ticket?',
    a: 'No printing required. You can show your ticket from your phone — just make sure the QR code is fully visible and your screen brightness is up when you arrive.',
  },
  {
    q: 'What happens if I don\u2019t receive my ticket email?',
    a: 'First check your spam, junk or promotions folder. If it\u2019s not there, sign in to VinePass and open "My Tickets" — you can re-download your ticket PDF from there at any time.',
  },
  {
    q: 'Can I buy tickets for someone else?',
    a: 'Yes. You can purchase tickets on behalf of friends, family or guests. We capture the lead guest\u2019s details at checkout and, where the event requires it, individual guest names per ticket. Just make sure the ticket holder has access to the QR code on the day.',
  },
  {
    q: 'How are VinePass service fees calculated?',
    a: 'VinePass charges a simple, transparent service fee based on the ticket price.\n\n• Under R150 — R10\n• R150\u2013R299 — R12\n• R300\u2013R599 — R15\n• R600 and above — R20\n\nThere are no monthly subscriptions, setup fees, or hidden charges.',
  },
  {
    q: 'Are there any additional commissions?',
    a: 'No. The published service fee is the only platform fee charged. VinePass does not charge an additional commission on top of the service fee.',
  },
  {
    q: 'Can I get a refund?',
    a: 'Refunds depend on the policy set by the event organiser — some events offer refunds up to a cut-off date, others are non-refundable. The refund window for each event is displayed on the event page before you book, so always check it first. Booking fees are non-refundable. Approved refunds are processed within 7\u201310 business days.',
  },
  {
    q: 'Can I transfer my ticket to someone else?',
    a: 'Unless the organiser states otherwise, you may share your ticket with someone else. Each QR code can only be scanned once, so make sure the person attending has the right QR code on the day.',
  },
  {
    q: 'What happens if my ticket has already been scanned?',
    a: 'Once a ticket has been scanned and checked in, it cannot be used again. This prevents duplicate entry. If you believe your ticket was scanned in error, please speak to the organiser at the venue.',
  },
  {
    q: 'Are VinePass events only for wine farms?',
    a: 'Wine farms and curated hospitality are our heart, but we also host food and wine pairings, sunset concerts, lifestyle markets, vineyard picnics, workshops and other premium local experiences.',
  },
  {
    q: 'Are under-18s allowed at events?',
    a: 'This depends on the event. Some wine-related experiences are strictly 18+, while family-friendly events welcome children. Age restrictions are listed on each event page — always check before booking.',
  },
  {
    q: 'Who do I contact if I have a question about an event?',
    a: 'For venue rules, dietary requirements, seating, weather plans or arrival times, contact the organiser directly using the details listed on the event page. For account, payment or ticket-delivery issues, email tickets@vinepass.co.za.',
  },
];

export const ORGANISER_FAQS = [
  {
    q: 'What is VinePass for organisers?',
    a: 'VinePass helps wine farms, event organisers and curated experience hosts sell tickets online, manage guest lists, issue digital tickets and check guests in via QR code on event day.',
  },
  {
    q: 'What type of events can I list on VinePass?',
    a: 'Wine tastings, food and wine pairings, harvest experiences, vineyard picnics, sunset concerts, workshops, markets, launch events, private experiences and other curated hospitality events.',
  },
  {
    q: 'How does VinePass help my event?',
    a: 'You get a polished booking page, secure online payments, automatic digital tickets, a real-time guest list with CSV export, in-app QR scanning, optional team scanning, and full transparency on sales and payouts.',
  },
  {
    q: 'Do guests receive tickets automatically?',
    a: 'Yes. The moment payment is confirmed, guests receive their ticket confirmation email with a QR code and PDF attachment. Their ticket is also saved to "My Tickets" in their VinePass account for easy access.',
  },
  {
    q: 'How does check-in work?',
    a: 'Each ticket has a unique QR code. On event day, you (or a team member you\u2019ve invited) open the VinePass scanner from your dashboard and scan tickets to validate entry. Scanned tickets can\u2019t be re-used, so duplicates are blocked automatically.',
  },
  {
    q: 'Can I view my guest list?',
    a: 'Yes. Your dashboard shows a real-time guest list per event with names, ticket types, check-in status and order details. You can search, edit guest details, and export the full list to CSV.',
  },
  {
    q: 'Can I add team members or associates to help scan tickets?',
    a: 'Yes. Invite associate team members from your dashboard. They receive a secure invitation link and, once accepted, can scan tickets for the events you assign them to — without seeing any financial data.',
  },
  {
    q: 'How do payouts work?',
    a: 'Ticket funds collect securely inside VinePass. When you\u2019re ready, request a payout from your dashboard. The admin team verifies the request and pays out via EFT to the banking details you onboarded with — typically within 7\u201310 business days of approval, less any applicable fees or refunds. You\u2019ll receive an email with a settlement PDF when the payout is paid.',
  },
  {
    q: 'How much are booking fees, and who pays them?',
    a: 'VinePass uses a flat, transparent service fee per ticket — never a percentage. The fee is R10 for tickets under R150, R12 for R150\u2013R299, R15 for R300\u2013R599, and R20 for R600 and above. There are no monthly subscriptions, setup fees, or hidden charges. When creating an event you can choose who covers the fee: the customer pays it on top of the ticket price, the organiser absorbs it, or it\u2019s split 50/50.',
  },
  {
    q: 'Can VinePass help set up my event listing?',
    a: 'Yes. Our team can assist with onboarding, event setup, ticket types, descriptions, pricing and imagery so your listing looks polished and on-brand. Reach out to info@vinepass.co.za to get started.',
  },
  {
    q: 'Can I create multiple ticket types?',
    a: 'Yes. Add as many ticket types as you need per event — general admission, early bird, VIP, table bookings, tasting-only, group tickets — each with its own price and capacity.',
  },
  {
    q: 'Can I limit ticket quantities?',
    a: 'Yes. Each ticket type has its own capacity limit, and the system automatically stops selling once the limit is reached so you never oversell.',
  },
  {
    q: 'Does VinePass support recurring or multi-day events?',
    a: 'Currently each listing represents a single date. For recurring or multi-day experiences, you can create one event per occurrence today — and we have native multi-date support on our roadmap. Email us if you\u2019d like to be notified when it ships.',
    comingSoon: true,
  },
  {
    q: 'What makes VinePass different from general ticketing platforms?',
    a: 'VinePass is built specifically around curated wine, hospitality and lifestyle experiences in South Africa. Our goal is not only to sell tickets but to give your event a booking journey that matches the quality of the experience itself.',
  },
  {
    q: 'How do I get my event listed?',
    a: 'Sign up as an organiser via "List Your Event", or email info@vinepass.co.za with your event details and we\u2019ll guide you through onboarding (including banking details for payouts) and help you publish your first listing.',
  },
];

// Curated subset for the homepage preview (4\u20136 high-impact guest Qs)
export const GUEST_FAQ_PREVIEW_KEYS = [
  'How do I buy tickets on VinePass?',
  'How will I receive my ticket?',
  'Do I need to print my ticket?',
  'How much are booking fees?',
  'Can I get a refund?',
];

// Curated subset for the organiser section preview (4\u20136 high-impact organiser Qs)
export const ORGANISER_FAQ_PREVIEW_KEYS = [
  'What type of events can I list on VinePass?',
  'How do payouts work?',
  'How much are booking fees, and who pays them?',
  'Can I add team members or associates to help scan tickets?',
  'How do I get my event listed?',
];

export const getGuestFaqPreview = () =>
  GUEST_FAQS.filter((f) => GUEST_FAQ_PREVIEW_KEYS.includes(f.q));

export const getOrganiserFaqPreview = () =>
  ORGANISER_FAQS.filter((f) => ORGANISER_FAQ_PREVIEW_KEYS.includes(f.q));
